import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import customAxios from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import { calculateArea, groupByMapLabel } from '@jumbo/utils/mapHelpers';

/**
 * Custom hook to fetch and manage map view data
 * @param {number} siteAssessmentID - The site assessment ID
 * @returns {Object} - Map data, samples, routes, and loading states
 */
const useMapViewData = siteAssessmentID => {
  const dispatch = useDispatch();

  // Map data states
  const [mapData, setMapData] = useState([]);
  const [pins, setPins] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [lines, setLines] = useState([]);

  // Samples data
  const [samples, setSamples] = useState([]);
  const [samplesData, setSamplesData] = useState([]);

  // Route data
  const [routeDetails, setRouteDetails] = useState([]);
  const [allLocationDetails, setAllLocationDetails] = useState([]);

  // Site data
  const [siteData, setSiteData] = useState(null);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all data when siteAssessmentID changes
  useEffect(() => {
    if (siteAssessmentID) {
      fetchAllMapData();
    }
  }, [siteAssessmentID]);

  // Process map data into pins, polygons, and lines
  useEffect(() => {
    if (mapData.length > 0) {
      const pinData = mapData.filter(item => item.mapType === 'pin');
      setPins(pinData);

      const linesGrouped = groupByMapLabel(mapData, 'line');
      setLines(linesGrouped);

      const polygonsGrouped = groupByMapLabel(mapData, 'polygon');
      setPolygons(Object.keys(polygonsGrouped).length ? calculateArea(polygonsGrouped) : []);
    }
  }, [mapData]);

  const fetchAllMapData = async () => {
    setIsLoading(true);
    dispatch({ type: FETCH_START });

    try {
      // Fetch site details
      await fetchSiteDetails();

      // Fetch map coordinates
      await fetchMapCoordinates();

      // Fetch samples
      await fetchSamples();

      // Fetch route details
      await fetchRouteDetails();

      // Fetch location details
      await fetchLocationDetails();

      dispatch({ type: FETCH_SUCCESS });
    } catch (error) {
      console.error('Error fetching map data:', error);
      dispatch({ type: FETCH_ERROR, payload: 'Failed to fetch map data' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSiteDetails = async () => {
    try {
      const response = await customAxios.get(`/CRM/GetCRMSiteDetails?siteAssessmentId=${siteAssessmentID}`, {
        headers: authHeader(),
      });

      if (response.status === 200 && response.data.length > 0) {
        setSiteData(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching site details:', error);
    }
  };

  const fetchMapCoordinates = async () => {
    try {
      const mapTypes = ['pin', 'polygon', 'line'];

      const responseArrays = await Promise.all(
        mapTypes.map(async type => {
          const response = await customAxios.get(
            `/SiteDetails/GetSiteDetailsMapCoordinates/${siteAssessmentID}?mapType=${type}`,
            { headers: authHeader() },
          );
          return response.data;
        }),
      );

      const consolidatedArray = responseArrays.flat();
      setMapData(consolidatedArray);
    } catch (error) {
      console.error('Error fetching map coordinates:', error);
    }
  };

  const fetchSamples = async () => {
    try {
      const [samplesResponse, samplesDataResponse] = await Promise.all([
        customAxios.get(`/SiteDetails/GetSiteDetailsSamples/${siteAssessmentID}`, {
          headers: authHeader(),
        }),
        customAxios.get(`/SiteDetails/GetSiteDetailsSamplesData/${siteAssessmentID}`, {
          headers: authHeader(),
        }),
      ]);

      if (samplesResponse.status === 200) {
        setSamples(samplesResponse.data);
      }

      if (samplesDataResponse.status === 200) {
        setSamplesData(samplesDataResponse.data);
      }
    } catch (error) {
      console.error('Error fetching samples:', error);
    }
  };

  const fetchRouteDetails = async () => {
    try {
      const [allLocationsRes, routesRes] = await Promise.all([
        customAxios.get(`/CRM/GetCustomerLocations?siteAssesmentID=${siteAssessmentID}`, {
          headers: authHeader(),
        }),
        customAxios.get(`/SiteDetails/GetSiteDetailsMapRoutes/${siteAssessmentID}`, {
          headers: authHeader(),
        }),
      ]);

      const allLocations = allLocationsRes.data || [];
      const allRoutes = routesRes.data || [];

      // Create a map of routes by destination for quick lookup
      const routeMap = new Map(allRoutes.map(route => [route.destination, route]));

      // Merge allLocations with existing route data (if any)
      const mergedRoutes = allLocations.map(loc => {
        const route = routeMap.get(loc.destinationName);
        return {
          siteAssessmentID: siteAssessmentID,
          destination: loc.destinationName,
          destinationLatitude: loc.latitude,
          destinationLongitude: loc.longitude,
          entranceLatitude: route?.entranceLatitude || null,
          entranceLongitude: route?.entranceLongitude || null,
          entrance: route?.entrance || '',
          polyLine: route?.polyLine || '',
          distance: route?.distance || null,
          ...(route?.id ? { id: route.id } : {}),
        };
      });

      setRouteDetails(mergedRoutes);
    } catch (error) {
      console.error('Error fetching route details:', error);
    }
  };

  const fetchLocationDetails = async () => {
    try {
      const response = await customAxios.get('/CRM/GetCustomerLocations', {
        headers: authHeader(),
      });

      if (response.status === 200) {
        setAllLocationDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching location details:', error);
    }
  };

  return {
    // Map data
    mapData,
    pins,
    polygons,
    lines,

    // Samples
    samples,
    samplesData,

    // Routes
    routeDetails,
    allLocationDetails,

    // Site data
    siteData,

    // Loading state
    isLoading,

    // Refresh function
    refetch: fetchAllMapData,
  };
};

export default useMapViewData;
